/**
 * MitzvotFilters - Panel de filtros avanzados
 * Filtrado por tipo, perspectiva, categorías, libro, parashá, rango de números
 */

import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_BASE = 'http://localhost:7890/api';

const MitzvotFilters = ({ filters, onFilterChange, onClearFilters }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Cargar categorías de Hebrew Roots
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await axios.get(`${API_BASE}/mitzvot/categorias`);
        if (response.data.success) {
          setCategories(response.data.data || []);
        }
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  return (
    <div className="space-y-4">
      {/* Filtros principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Tipo de mandamiento */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo
          </label>
          <select
            value={filters.tipo}
            onChange={(e) => onFilterChange('tipo', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos</option>
            <option value="A">Hacer (Asé)</option>
            <option value="L">No Hacer (Lo Tasé)</option>
          </select>
        </div>

        {/* Perspectiva */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Perspectiva
          </label>
          <select
            value={filters.perspectiva || ''}
            onChange={(e) => onFilterChange('perspectiva', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todas las perspectivas</option>
            <option value="rambam">Rambam (Maimónides)</option>
            <option value="ramban">Ramban (Najmánides)</option>
            <option value="chinuch">Chinuch</option>
            <option value="semag">SEMAG</option>
            <option value="bahag">BAHAG</option>
            <option value="hebrewRoots">Raíces Hebreas</option>
          </select>
        </div>

        {/* Rango de números - Mínimo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Desde #
          </label>
          <input
            type="number"
            min="1"
            max="613"
            value={filters.minNumber}
            onChange={(e) => onFilterChange('minNumber', e.target.value)}
            placeholder="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Rango de números - Máximo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hasta #
          </label>
          <input
            type="number"
            min="1"
            max="613"
            value={filters.maxNumber}
            onChange={(e) => onFilterChange('maxNumber', e.target.value)}
            placeholder="613"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Botón para mostrar filtros avanzados */}
      <div>
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {showAdvancedFilters ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Ocultar filtros avanzados
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Mostrar filtros avanzados (Categorías, Ordenamiento)
            </>
          )}
        </button>
      </div>

      {/* Filtros avanzados */}
      {showAdvancedFilters && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          {/* Categorías de Hebrew Roots */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categorías de Raíces Hebreas ({categories.length})
            </label>
            {loadingCategories ? (
              <p className="text-sm text-gray-500">Cargando categorías...</p>
            ) : categories.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border border-gray-200">
                {categories.map((category) => (
                  <label
                    key={category.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-2 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={filters.categorias?.includes(category.id) || false}
                      onChange={(e) => {
                        const currentCategories = filters.categorias || [];
                        const newCategories = e.target.checked
                          ? [...currentCategories, category.id]
                          : currentCategories.filter(id => id !== category.id);
                        onFilterChange('categorias', newCategories);
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{category.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No hay categorías disponibles</p>
            )}
          </div>

          {/* Ordenamiento y otros filtros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ordenar por
              </label>
              <select
                value={filters.orderBy}
                onChange={(e) => onFilterChange('orderBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="mitzvahNumber">Número de Mandamiento</option>
                <option value="mitzvahNameEs">Nombre (Español)</option>
                <option value="mitzvahName">Nombre (Hebreo)</option>
                <option value="createdAt">Fecha de Creación</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección
              </label>
              <select
                value={filters.orderDir}
                onChange={(e) => onFilterChange('orderDir', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ASC">Ascendente (A-Z, 1-9)</option>
                <option value="DESC">Descendente (Z-A, 9-1)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resultados por página
              </label>
              <select
                value={filters.limit}
                onChange={(e) => onFilterChange('limit', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Botón de limpiar filtros */}
      <div className="flex justify-end">
        <button
          onClick={onClearFilters}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
          Limpiar filtros
        </button>
      </div>
    </div>
  );
};

export default MitzvotFilters;
